import React, { useEffect, useState } from 'react'

import { CopilotOptionItem, RefineReason } from '@/api/client'
import { QueueItem } from './askTypes'

/**
 * Short, user-facing label for each reason the server flags. The full
 * explanatory text comes from `needs_confirmation_message` (server-
 * authored) and is shown alongside.
 */
const REASON_TITLE: Record<RefineReason, string> = {
  past_date: 'Deadline is in the past',
  categorical: 'Multiple outcomes detected',
  ambiguous: 'Needs clarification',
  low_confidence: 'Double-check this question',
}

interface QueueConfirmationCardProps {
  item: QueueItem
  onClarify: (question: string) => void
  onConfirm: (question: string) => void
  onDecompose: (questions: string[]) => void
  onCancel: () => void
}

/**
 * Review card shown when the refinement copilot flags a question as
 * ambiguous or categorical. Lets the user:
 *  - edit the normalized question directly,
 *  - pick one of the copilot's suggested clarifications/normalizations,
 *  - for categorical questions, select which outcomes to forecast as
 *    separate binary questions.
 */
export default function QueueConfirmationCard({
  item,
  onClarify,
  onConfirm,
  onDecompose,
  onCancel,
}: QueueConfirmationCardProps) {
  const r = item.refinement
  if (!r) return null

  const analysis = r.analysis
  const initialQuestion = analysis.normalized_question || analysis.raw_question || item.question
  const [editedQuestion, setEditedQuestion] = useState(initialQuestion)
  const decomp = r.suggested_binary_decomposition || []
  const copilot = r.copilot || null
  const nextClarification = copilot?.next_clarification || null
  const unresolvedSlots = copilot?.unresolved_slots || copilot?.clarification_questions || []
  const binaryTemplates = copilot?.suggested_binary_templates || []
  const completeBinaryTemplates = binaryTemplates.filter((q) => !q.includes('['))
  const [selectedOutcomes, setSelectedOutcomes] = useState<Set<number>>(() => new Set(decomp.map((_, i) => i)))
  const hasPlaceholder = (value: string) => value.includes('[') || value.includes(']')

  // Server-drafted concrete resolution. When present we render an
  // editable "Proposed resolution" block instead of the four-option
  // generic dropdown. Track the edited text so the user can tweak it
  // before accepting; unchanged accept → pass through the original
  // criterion back to the server via onClarify, which re-refines with
  // the explicit text baked into the question.
  const proposed = copilot?.proposed_resolution || null
  const [editedResolution, setEditedResolution] = useState(proposed?.resolution_criteria || '')
  useEffect(() => {
    setEditedResolution(proposed?.resolution_criteria || '')
  }, [proposed?.resolution_criteria])

  useEffect(() => {
    setEditedQuestion(initialQuestion)
  }, [initialQuestion])

  // Re-sync outcome selection when the refinement returns a different decomposition.
  const decompKey = decomp.join('|')
  useEffect(() => {
    setSelectedOutcomes(new Set(decomp.map((_, i) => i)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decompKey])

  /**
   * Applies a copilot clarification option.
   * Dispatches on the structured `id` (preferred — matches the backend
   * _OPTION_SLUG_OVERRIDES map) and falls back to the human label only
   * when no id is present (older servers).
   */
  function applySuggestion(opt: string | CopilotOptionItem, slot?: string) {
    const asItem = typeof opt === 'string' ? { id: '', label: opt } as CopilotOptionItem : opt
    const optId = String(asItem.id || '').trim()
    const label = String(asItem.label || '').trim()
    const match = (id: string) => optId === id || (!optId && label === id)
    if (!label) return

    let nextQuestion = editedQuestion.trim() || analysis.raw_question || item.question

    const currentYear = new Date().getFullYear()
    const replaceDateToken = (replacement: string) => {
      if (nextQuestion.includes('[date]')) {
        nextQuestion = nextQuestion.replace('[date]', replacement)
        return true
      }
      return false
    }

    if (match('end_of_year') || match('End of this year')) {
      if (!replaceDateToken(`December 31, ${currentYear}`)) {
        nextQuestion = `${nextQuestion.replace(/\?$/, '')} by December 31, ${currentYear}?`
      }
      setEditedQuestion(nextQuestion)
      onClarify(nextQuestion)
      return
    }
    if (match('specific_date') || match('Specific date')) {
      if (!replaceDateToken('[specific date]')) {
        nextQuestion = `${nextQuestion.replace(/\?$/, '')} by [specific date]?`
      }
      setEditedQuestion(nextQuestion)
      return
    }
    if (match('specific_month_year') || match('Specific month/year')) {
      if (!replaceDateToken('[month/year]')) {
        nextQuestion = `${nextQuestion.replace(/\?$/, '')} by [month/year]?`
      }
      setEditedQuestion(nextQuestion)
      return
    }
    if ((match('single_winner') || match('Single winner question')) && copilot?.suggested_normalizations?.[0]) {
      nextQuestion = copilot.suggested_normalizations[0]
      setEditedQuestion(nextQuestion)
      onClarify(nextQuestion)
      return
    }
    if (
      (match('separate_binaries')
        || match('Separate binary questions per party/candidate')
        || match('Separate binary questions')
        || match('Use binary questions instead'))
      && completeBinaryTemplates.length > 0
    ) {
      onDecompose(completeBinaryTemplates)
      return
    }
    if ((match('victoria_au_state') || match('Victoria state election in Australia')) && copilot?.suggested_normalizations?.[0]) {
      nextQuestion = copilot.suggested_normalizations[0]
      setEditedQuestion(nextQuestion)
      onClarify(nextQuestion)
      return
    }
    if ((match('name_candidates') || match('Name the candidates/parties explicitly')) && completeBinaryTemplates.length > 0) {
      onDecompose(completeBinaryTemplates)
      return
    }

    if (slot === 'success_condition') {
      const base = nextQuestion.replace(/\?$/, '')
      if ((match('official_announcement') || match('Official announcement')) && !/announce|announcement/i.test(base)) {
        nextQuestion = `${base} (count only an official announcement)?`
      } else if ((match('formal_launch') || match('Formal launch/release')) && !/launch|release/i.test(base)) {
        nextQuestion = `${base} (count only a formal launch or release)?`
      } else if ((match('regulatory_filing') || match('Regulatory filing/decision')) && !/filing|decision|approval/i.test(base)) {
        nextQuestion = `${base} (count only a formal regulatory filing or decision)?`
      } else if ((match('specific_threshold') || match('Specific threshold reached')) && !/threshold|reach|exceed|above|below|over|under/i.test(base)) {
        nextQuestion = `${base} (use a specific measurable threshold)?`
      }
      setEditedQuestion(nextQuestion)
      return
    }

    // Fallback: append the option as a lightweight clarifying note into the question text.
    if (!nextQuestion.toLowerCase().includes(label.toLowerCase())) {
      nextQuestion = `${nextQuestion.replace(/\?$/, '')} (${label})?`
    }
    setEditedQuestion(nextQuestion)
    if (!hasPlaceholder(nextQuestion) && slot !== 'success_condition') {
      onClarify(nextQuestion)
    }
  }

  /** Normalise a clarification's options to `{id,label}[]`. Prefers the
   * structured `option_items` from the server; falls back to the legacy
   * `options: string[]` array with synthesised (empty) ids. */
  function normaliseOptions(cl: { options?: string[]; option_items?: CopilotOptionItem[] } | null | undefined): CopilotOptionItem[] {
    if (!cl) return []
    if (Array.isArray(cl.option_items) && cl.option_items.length > 0) return cl.option_items
    if (Array.isArray(cl.options)) return cl.options.map((s) => ({ id: '', label: s }))
    return []
  }

  const toggleOutcome = (idx: number) => {
    setSelectedOutcomes((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const reason = (r.needs_confirmation_reason || null) as RefineReason | null
  const reasonTitle = reason && REASON_TITLE[reason] ? REASON_TITLE[reason] : 'Review before forecasting'
  const reasonMessage = r.needs_confirmation_message || null

  return (
    <div className="ask-confirm-card">
      <div className="ask-confirm-header">
        <span className={`pill ${reason === 'past_date' ? 'bad' : reason === 'categorical' ? '' : ''}`}>
          {reasonTitle}
        </span>
      </div>
      {reasonMessage && (
        <div
          className="muted"
          style={{ fontSize: 12, lineHeight: 1.55, marginTop: 2, marginBottom: 6 }}
          role="note"
        >
          {reasonMessage}
        </div>
      )}

      <div className="ask-confirm-body">
        <div className="ask-confirm-row">
          <span className="ask-confirm-label">Question type</span>
          <span className="ask-confirm-value">{analysis.question_type}</span>
        </div>
        <div className="ask-confirm-row">
          <span className="ask-confirm-label">Category</span>
          <span className="ask-confirm-value">{analysis.category}</span>
        </div>
        {analysis.target_date && (
          <div className="ask-confirm-row">
            <span className="ask-confirm-label">Target date</span>
            <span className="ask-confirm-value">{analysis.target_date}</span>
          </div>
        )}
        {analysis.resolution_criteria && !proposed && (
          <div className="ask-confirm-row">
            <span className="ask-confirm-label">Resolution</span>
            <span className="ask-confirm-value">{analysis.resolution_criteria}</span>
          </div>
        )}

        {/* Drafted resolution — replaces the old "What exact outcome
            should count as YES?" dropdown. The user can accept, edit,
            or re-draft. When auto_applied=true the server already wrote
            this into analysis.resolution_criteria; we still render the
            block so the change is visible. */}
        {proposed && (
          <div className="ask-confirm-row" style={{ marginTop: 4 }}>
            <span className="ask-confirm-label">
              {proposed.auto_applied ? 'Resolution (drafted)' : 'Proposed resolution'}
            </span>
            <div style={{ display: 'grid', gap: 6 }}>
              <textarea
                className="input"
                value={editedResolution}
                onChange={(e) => setEditedResolution(e.target.value)}
                rows={3}
                aria-label="Proposed resolution criteria"
                style={{ fontSize: 13, lineHeight: 1.5 }}
              />
              {proposed.success_event && (
                <div className="muted" style={{ fontSize: 11 }}>
                  YES event: {proposed.success_event}
                </div>
              )}
              {proposed.resolution_sources.length > 0 && (
                <div className="muted" style={{ fontSize: 11 }}>
                  Sources:{' '}
                  {proposed.resolution_sources.slice(0, 4).map((s, i) => {
                    const isUrl = /^https?:\/\//i.test(s)
                    return (
                      <React.Fragment key={i}>
                        {i > 0 ? ', ' : null}
                        {isUrl
                          ? <a href={s} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{s}</a>
                          : <span>{s}</span>}
                      </React.Fragment>
                    )
                  })}
                </div>
              )}
              {proposed.confidence < 0.75 && proposed.needs_clarification && (
                <div className="muted" style={{ fontSize: 11, color: 'var(--warn)' }}>
                  Low-confidence draft: {proposed.needs_clarification}
                </div>
              )}

              {/* Alternative scope variants — one-click swap-ins for the
                  textarea. Each chip addresses a specific ambiguity the
                  drafter identified (e.g. "combat forces only" vs "all
                  active-duty"), so the user isn't left hand-writing the
                  scope decision themselves. */}
              {Array.isArray(proposed.alternative_variants) && proposed.alternative_variants.length > 0 && (
                <div style={{ display: 'grid', gap: 4 }}>
                  <div className="muted" style={{ fontSize: 11 }}>
                    Alternative scopes — click to swap in:
                  </div>
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {proposed.alternative_variants.map((v, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`btn small ${editedResolution.trim() === v.criterion.trim() ? 'primary' : ''}`}
                        title={v.addresses_ambiguity
                          ? `Resolves: ${v.addresses_ambiguity}`
                          : v.criterion}
                        onClick={() => setEditedResolution(v.criterion)}
                        style={{ fontSize: 12 }}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <button
                  className="btn small primary"
                  type="button"
                  title="Use this resolution criterion and forecast"
                  onClick={() => {
                    // Append the accepted criterion onto the question text
                    // the same way /refine encodes it into the normalized
                    // form — so the next refine call sees an unambiguous
                    // question and the auto-confirm path takes over.
                    const base = (editedQuestion || analysis.normalized_question || item.question).replace(/\?$/, '')
                    const note = editedResolution.trim()
                    const next = note
                      ? `${base} — resolves YES if: ${note}`
                      : base
                    setEditedQuestion(next)
                    onClarify(next)
                  }}
                  disabled={!editedResolution.trim()}
                >
                  Accept &amp; re-check
                </button>
                <button
                  className="btn small"
                  type="button"
                  title="Revert the draft — the copilot will not propose a resolution again"
                  onClick={() => setEditedResolution('')}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {analysis.ambiguities.length > 0 && (
          <div className="ask-confirm-row">
            <span className="ask-confirm-label">Ambiguities</span>
            <ul className="ask-confirm-list">
              {analysis.ambiguities.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}

        {copilot?.note && (
          <div
            className="ask-confirm-row"
            style={copilot.resolution_draft_empty ? {
              borderLeft: '3px solid var(--warn)',
              paddingLeft: 10,
              background: 'rgba(251, 191, 36, 0.06)',
              borderRadius: 6,
              marginTop: 4,
            } : undefined}
          >
            <span className="ask-confirm-label">Co-pilot</span>
            <div
              className="ask-confirm-value"
              style={{ lineHeight: 1.55 }}
              role={copilot.resolution_draft_empty ? 'note' : undefined}
            >
              {copilot.note}
            </div>
          </div>
        )}

        {nextClarification && (() => {
          const items = normaliseOptions(nextClarification)
          return (
            <div className="ask-confirm-row">
              <span className="ask-confirm-label">Clarify</span>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'grid', gap: 6 }}>
                  <div className="ask-confirm-value" style={{ lineHeight: 1.45 }}>{nextClarification.prompt}</div>
                  {items.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {items.map((item, j) => (
                        <button
                          key={item.id || j}
                          className="btn small"
                          type="button"
                          onClick={() => applySuggestion(item, nextClarification.slot)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {unresolvedSlots.length > 1 && (
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {unresolvedSlots.length} details still need to be worked through.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })()}

        {Array.isArray(copilot?.suggested_normalizations) && copilot!.suggested_normalizations!.length > 0 && (
          <div className="ask-confirm-row">
            <span className="ask-confirm-label">Suggested questions</span>
            <div style={{ display: 'grid', gap: 8 }}>
              {copilot!.suggested_normalizations!.map((sq, i) => (
                <div key={i} className="ask-confirm-decompose-item" style={{ alignItems: 'flex-start' }}>
                  <span className="ask-confirm-decompose-text">{sq}</span>
                  <button className="btn small" type="button" onClick={() => { setEditedQuestion(sq); onClarify(sq) }}>Use</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Editable normalized question */}
        <div className="ask-confirm-row" style={{ marginTop: 8 }}>
            <span className="ask-confirm-label">Normalized question</span>
            <input
              className="input"
              value={editedQuestion}
              onChange={(e) => setEditedQuestion(e.target.value)}
              placeholder={analysis.raw_question || item.question}
              style={{ fontSize: 13, marginTop: 4 }}
            />
            {Array.isArray(proposed?.suggested_question_rewrites) &&
              (proposed?.suggested_question_rewrites?.length ?? 0) > 0 && (
                <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
                  <div className="muted" style={{ fontSize: 11 }}>
                    Tighten the question — click to swap in:
                  </div>
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {proposed!.suggested_question_rewrites!.map((rw, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`btn small ${editedQuestion.trim() === rw.question.trim() ? 'primary' : ''}`}
                        title={rw.fixes ? `Fixes: ${rw.fixes}` : rw.question}
                        onClick={() => setEditedQuestion(rw.question)}
                        style={{ fontSize: 12 }}
                        aria-label={`Apply rewrite: ${rw.label}`}
                      >
                        {rw.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
          </div>

        {/* Categorical: multi-select outcomes for decomposition */}
        {r.is_categorical && decomp.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div className="ask-confirm-label" style={{ marginBottom: 6 }}>
              This is a multi-outcome question. Select which outcomes to forecast:
            </div>
            <div className="ask-confirm-decompose-list">
              {decomp.map((q, i) => (
                <label key={i} className="ask-confirm-decompose-item">
                  <input
                    type="checkbox"
                    checked={selectedOutcomes.has(i)}
                    onChange={() => toggleOutcome(i)}
                  />
                  <span className="ask-confirm-decompose-text">{q}</span>
                </label>
              ))}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                className="btn primary"
                onClick={() => onDecompose(decomp.filter((_, i) => selectedOutcomes.has(i)))}
                disabled={selectedOutcomes.size === 0}
                type="button"
              >
                Forecast {selectedOutcomes.size} selected outcome{selectedOutcomes.size !== 1 ? 's' : ''}
              </button>
              <button
                className="btn"
                onClick={() => setSelectedOutcomes(new Set(decomp.map((_, i) => i)))}
                type="button"
              >
                Select all
              </button>
              <button
                className="btn"
                onClick={() => setSelectedOutcomes(new Set())}
                type="button"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {r.is_categorical && decomp.length === 0 && binaryTemplates.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div className="ask-confirm-label" style={{ marginBottom: 6 }}>
              Suggested binary questions
            </div>
            <div className="ask-confirm-decompose-list">
              {binaryTemplates.map((q, i) => (
                <div key={i} className="ask-confirm-decompose-item" style={{ alignItems: 'flex-start' }}>
                  <span className="ask-confirm-decompose-text">{q}</span>
                  {!q.includes('[') && (
                    <button className="btn small" type="button" onClick={() => onDecompose([q])}>Use</button>
                  )}
                </div>
              ))}
            </div>
            {completeBinaryTemplates.length > 1 && (
              <div style={{ marginTop: 8 }}>
                <button className="btn primary" type="button" onClick={() => onDecompose(completeBinaryTemplates)}>
                  Forecast all suggested binary questions
                </button>
              </div>
            )}
          </div>
        )}

        {/* Non-categorical or user wants to forecast as-is */}
        <div className="ask-confirm-actions">
          <button
            className="btn"
            onClick={() => onClarify(editedQuestion.trim() || analysis.normalized_question || analysis.raw_question || item.question)}
            disabled={!editedQuestion.trim() || hasPlaceholder(editedQuestion.trim())}
            type="button"
          >
            Re-check with copilot
          </button>
          <button
            className="btn"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={() => onConfirm(editedQuestion.trim() || analysis.normalized_question)}
            disabled={!editedQuestion.trim()}
            type="button"
          >
            {r.is_categorical ? 'Use clarified question' : 'Confirm & forecast'}
          </button>
        </div>
      </div>
    </div>
  )
}
